import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  SuperadminProfileEditForm,
  SuperadminProfileEditInitialValues,
  SuperadminProfileComponents,
  SuperadminProfileAdditionalParams,
  SuperadminProfileFormOptions,
  onSuperadminProfileFormSaved,
  ISuperadminProfileFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedSuperadminProfile, ISharedPatchSuperadminProfile, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchSuperadminProfile, patchSuperadminProfile, getSuperadminProfileUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedSuperadminProfile> {}

const SuperadminProfileFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchSuperadminProfile(id).then(superadmin_profile => setValue(superadmin_profile));
  }, [id, setValue, ]);

  const param_props: ISuperadminProfileFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && SuperadminProfileEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && SuperadminProfileFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchSuperadminProfile, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchSuperadminProfile) => value && save(() => patchSuperadminProfile(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onSuperadminProfileFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.SuperadminProfile, value)}
    >
      <AtlasForm<ISharedPatchSuperadminProfile>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={SuperadminProfileComponents}
        additionalParams={SuperadminProfileAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={SuperadminProfileEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('SUPERADMIN_PROFILE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const SuperadminProfileFormEditView = React.memo(withEdit(SuperadminProfileFormEdit, getSuperadminProfileUpdateSchema));
